import { ClipboardPaste } from 'lucide-react';
import { Pencil } from 'lucide-react';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Plus, Search, Filter, Trash2, Palette, Copy, ArrowUpDown, RotateCcw, Download, Eye,
  Calendar, Shield, Check, X, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown,
} from 'lucide-react';
import './Checklist.css';

const ORIGENS = ['WHATSAPP', 'INSTAGRAM', 'GOOGLE', 'INDICAÇÃO', 'WATI', 'TECH', 'PRESENCIAL', 'OUTRO'];
const REPIQUES = Array.from({ length: 20 }, (_, i) => String(i + 1));
const STATUS = ['NOVO', 'EM ATENDIMENTO', 'AGENDADO', 'CONFIRMADO', 'CONCLUÍDO', 'CANCELADO'];
const STATUS_CLASSE = {
  'NOVO': 'novo', 'EM ATENDIMENTO': 'atendimento', 'AGENDADO': 'agendado',
  'CONFIRMADO': 'confirmado', 'CONCLUÍDO': 'concluido', 'REALIZADO': 'concluido', 'CANCELADO': 'cancelado',
};
// [cor da linha, nome, cor da amostra no menu]
const CORES = [
  ['', 'Sem cor', 'transparent'], ['#e6f6ec', 'Verde', '#4cb98a'], ['#fff4d6', 'Amarelo', '#e8a93b'],
  ['#e3f0fb', 'Azul', '#5b9bd5'], ['#fde8e8', 'Vermelho', '#e07b7b'], ['#efe9fd', 'Roxo', '#9b7fd4'],
];
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const DIAS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

// "chave" é a coluna no banco; "rotulo" é o que aparece no cabeçalho.
const F = {
  data: { chave: 'data', rotulo: 'Data', tipo: 'date' },
  atendente: { chave: 'atendente', rotulo: 'Atendente', tipo: 'auto' },
  paciente: { chave: 'paciente', rotulo: 'Paciente', tipo: 'text', placeholder: 'Nome completo' },
  nome: { chave: 'paciente', rotulo: 'Nome', tipo: 'text', placeholder: 'Nome completo' },
  telefone: { chave: 'telefone', rotulo: 'Telefone', tipo: 'tel', placeholder: '(00) 00000-0000' },
  repique: { chave: 'repique', rotulo: 'Repique', tipo: 'combo', opcoes: REPIQUES, placeholder: '0' },
  origem: { chave: 'origem', rotulo: 'Origem', tipo: 'combo', opcoes: ORIGENS, placeholder: 'Origem' },
  servico: { chave: 'servico', rotulo: 'Serviço', tipo: 'text', placeholder: 'Serviço' },
  especialidade: { chave: 'especialidade', rotulo: 'Especialidade', tipo: 'text', placeholder: 'Especialidade' },
  procedimento: { chave: 'procedimento', rotulo: 'Procedimento', tipo: 'text', placeholder: 'Procedimento' },
  dentista: { chave: 'profissional', rotulo: 'Dentista', tipo: 'text', placeholder: 'Dentista' },
  associado: { chave: 'associado', rotulo: 'Associado', tipo: 'select', opcoes: ['SIM', 'NÃO'] },
  status: { chave: 'status', rotulo: 'Status', tipo: 'status', placeholder: 'Status' },
  observacao: { chave: 'observacoes', rotulo: 'Observação', tipo: 'text', placeholder: 'Observação' },
};

const CONFIG = {
  medico: {
    titulo: 'Médico', add: 'Adicionar paciente',
    colunas: [F.data, F.atendente, F.paciente, F.telefone, F.origem, F.servico, F.especialidade, F.status, F.repique],
  },
  odontologia: {
    titulo: 'Odontologia', add: 'Adicionar paciente',
    colunas: [F.data, F.atendente, F.paciente, F.telefone, F.origem, F.procedimento, F.dentista, F.status, F.observacao, F.repique],
  },
  cartao: {
    titulo: 'Cartão', add: 'Adicionar registro',
    colunas: [
      F.data,
      F.atendente,
      F.nome,
      F.associado,
      F.telefone,
      F.origem,
      F.especialidade,
      F.status,
      F.observacao,
      F.repique,
    ],
  },
};

async function api(path, options = {}) {
  const res = await fetch(`/api/checklist${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.error || 'Erro na requisição');
  }
  return res.json();
}

const pad = (n) => String(n).padStart(2, '0');
const isoDe = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const hojeISO = () => isoDe(new Date());
const fmtData = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '');
const formatTempoRepique = (inicio, agoraMs) => {
  if (!inicio) return '';
  const inicioMs = new Date(inicio).getTime();
  if (!Number.isFinite(inicioMs)) return '';
  const total = Math.max(0, Math.floor((agoraMs - inicioMs) / 60000));
  const dias = Math.floor(total / 1440);
  const horas = Math.floor((total % 1440) / 60);
  const minutos = total % 60;
  if (dias) return `${dias} dia${dias === 1 ? '' : 's'} ${horas}hs`;
  if (horas) return `${horas}hs ${minutos}min`;
  return `${minutos}min`;
};
const LIMITE_BUSCA_TODAS = 300;              // busca em todas as datas: mostra no máximo estas linhas (as mais bem colocadas na ordem)
const norm = (s) => String(s || '').trim().toUpperCase();
const classeStatus = (s) => STATUS_CLASSE[norm(s)] || 'outro';
const mascaraTelefone = (v) => {
  const d = (v || '').replace(/\D/g, '').slice(0, 11);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};
const umMesAtras = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d; };
const fmtHora = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
// Só para mostrar/esconder o botão; quem barra de verdade é o servidor.
const souAdmin = () => {
  try {
    const parte = localStorage.getItem('token').split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(parte), (c) => c.charCodeAt(0))));
    return (json.roles || []).some((r) => String(r).toLowerCase() === 'admin');
  } catch (e) {
    return false;
  }
};
const souAdminOuGestor = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const parte = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(new TextDecoder().decode(
      Uint8Array.from(atob(parte), (c) => c.charCodeAt(0))
    ));
    return (json.roles || []).some((r) =>
      ['admin', 'gestor'].includes(String(r).trim().toLowerCase())
    );
  } catch (e) {
    return false;
  }
};
const isRascunho = (id) => String(id).startsWith('rascunho-');
const linhaNova = () => ({
  id: `rascunho-${Date.now()}`, data: hojeISO(), atendente: '', paciente: '', telefone: '',
  origem: '', status: 'NOVO', servico: '', especialidade: '', procedimento: '',
  profissional: '', associado: '', observacoes: '', cor: '',
  repique: 0, repique_started_at: null, repique_pendente: false,
});
const valorCol = (i, k) => {
  if (k === 'data') return String(i.data || '').slice(0, 10);
  if (k === 'repique') return String(i.repique ?? 0);
  return norm(i[k]);
};

function Paleta({ atual, onEscolher }) {
  return (
    <div>
      <h4>Cor da linha</h4>
      {CORES.map(([valor, nome, forte]) => (
        <button key={nome} type="button" className="ck-pop-op" onClick={() => onEscolher(valor)}>
          <span className="ck-amostra" style={{ background: forte }} />
          {nome}
          {atual !== undefined && (atual || '') === valor && <Check size={14} className="ck-pop-check" />}
        </button>
      ))}
    </div>
  );
}

function Calendario({ valor, onEscolher }) {
  const ini = valor ? new Date(`${valor}T12:00:00`) : new Date();
  const [ano, setAno] = useState(ini.getFullYear());
  const [mes, setMes] = useState(ini.getMonth());
  const [modoMes, setModoMes] = useState(false);

  const mover = (n) => {
    if (modoMes) { setAno(ano + n); return; }
    const d = new Date(ano, mes + n, 1);
    setAno(d.getFullYear());
    setMes(d.getMonth());
  };
  const primeiro = new Date(ano, mes, 1);
  const dias = Array.from({ length: 42 }, (_, k) => new Date(ano, mes, 1 - primeiro.getDay() + k));
  const hoje = hojeISO();

  return (
    <div className="ck-cal">
      <div className="ck-cal-topo">
        <button type="button" className="ck-cal-mes" onClick={() => setModoMes((m) => !m)}>
          {modoMes ? ano : `${MESES[mes]} de ${ano}`} <ChevronDown size={12} />
        </button>
        <span className="ck-cal-setas">
          <button type="button" aria-label="Anterior" onClick={() => mover(-1)}><ChevronUp size={16} /></button>
          <button type="button" aria-label="Próximo" onClick={() => mover(1)}><ChevronDown size={16} /></button>
        </span>
      </div>
      {modoMes ? (
        <div className="ck-cal-meses">
          {MESES.map((m, k) => (
            <button key={m} type="button" className={k === mes ? 'ck-cal-sel' : ''} onClick={() => { setMes(k); setModoMes(false); }}>
              {m.slice(0, 3)}
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="ck-cal-grade ck-cal-sem">{DIAS.map((d, k) => <span key={k}>{d}</span>)}</div>
          <div className="ck-cal-grade">
            {dias.map((d) => {
              const v = isoDe(d);
              const cls = `ck-cal-dia${d.getMonth() !== mes ? ' fora' : ''}${v === hoje ? ' hoje' : ''}${v === valor ? ' ck-cal-sel' : ''}`;
              return <button key={v} type="button" className={cls} onClick={() => onEscolher(v)}>{d.getDate()}</button>;
            })}
          </div>
        </>
      )}
      <div className="ck-cal-rodape">
        <button type="button" onClick={() => onEscolher('')}>Limpar</button>
        <button type="button" onClick={() => onEscolher(hoje)}>Hoje</button>
      </div>
    </div>
  );
}

function FiltroColuna({ rotulo, escopo, opcoes, selecionados, onChange, onFechar }) {
  const [q, setQ] = useState('');
  const vis = opcoes.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()));
  const alternar = (v) => onChange(selecionados.includes(v) ? selecionados.filter((x) => x !== v) : [...selecionados, v]);
  return (
    <div>
      <div className="ck-fc-topo">
        <div><div className="ck-fc-peq">Filtrar coluna</div><b>{rotulo}</b>{escopo && <div className="ck-fc-escopo">{escopo}</div>}</div>
        <button type="button" className="ck-fc-x" aria-label="Fechar" onClick={onFechar}><X size={14} /></button>
      </div>
      <div className="ck-fc-busca">
        <Search size={13} />
        <input autoFocus placeholder="Pesquisar valor" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="ck-fc-acoes">
        <button type="button" onClick={() => onChange(opcoes.map((o) => o.valor))}>Selecionar tudo</button>
        <button type="button" onClick={() => onChange([])}>Limpar</button>
      </div>
      <div className="ck-fc-lista">
        {vis.length === 0 && <div className="ck-pop-aviso">Nenhum valor encontrado.</div>}
        {vis.map((o) => (
          <label key={o.valor || '__vazio'} className="ck-fc-item">
            <input type="checkbox" checked={selecionados.includes(o.valor)} onChange={() => alternar(o.valor)} /> {o.label}
          </label>
        ))}
      </div>
    </div>
  );
}

// ── Colar linhas copiadas em outra data ──
const COPIA_CHAVE = 'checklist:linhas-copiadas';
const COPIA_LIMITE = 100;                    // máximo de linhas coladas de uma vez
const COPIA_VALIDADE_MS = 12 * 60 * 60 * 1000;
const COLAR_COM_STATUS_NOVO = false;         // true = a linha colada volta para o status NOVO
const lerCopiadas = () => {
  try {
    const c = JSON.parse(sessionStorage.getItem(COPIA_CHAVE) || 'null');
    return c && Array.isArray(c.linhas) && Date.now() - c.em < COPIA_VALIDADE_MS ? c : null;
  } catch (err) { return null; }
};
const guardarCopiadas = (c) => {
  try { sessionStorage.setItem(COPIA_CHAVE, JSON.stringify(c)); } catch (err) { /* sem espaço: ignora */ }
};

// Larguras padrão das colunas (px); o usuário ajusta arrastando o cabeçalho.
const LARG_PADRAO = {
  data: 135, atendente: 120, paciente: 200, telefone: 130, repique: 110, origem: 130, servico: 150,
  especialidade: 140, procedimento: 150, profissional: 140, associado: 110, status: 150, observacoes: 220,
};
const LARG_MIN = 70;

export default function Checklist({ tipo }) {
  const tipoSeguro = CONFIG[tipo] ? tipo : 'medico';
  const cfg = CONFIG[tipoSeguro];
  const [telefoneEmEdicao, setTelefoneEmEdicao] = useState(null);
  const [larguras, setLarguras] = useState({});
  const largRef = useRef({});
  useEffect(() => {
    let l = {};
    try { l = JSON.parse(localStorage.getItem(`ck-larg-${tipoSeguro}`)) || {}; } catch (e) { l = {}; }
    largRef.current = l;
    setLarguras(l);
  }, [tipoSeguro]);
  const largDe = (c) => larguras[c.chave] || LARG_PADRAO[c.chave] || 150;
  const salvarLarg = (l) => { largRef.current = l; setLarguras(l); };
  const gravarLarg = () => {
    try { localStorage.setItem(`ck-larg-${tipoSeguro}`, JSON.stringify(largRef.current)); } catch (e) { /* sem armazenamento */ }
  };
  const iniciarResize = (e, chave) => {
    e.preventDefault();
    e.stopPropagation();
    const x0 = e.clientX;
    const atual = e.currentTarget.parentElement.offsetWidth;
    document.body.classList.add('ck-redimensionando');
    const mover = (ev) => salvarLarg({ ...largRef.current, [chave]: Math.max(LARG_MIN, Math.round(atual + ev.clientX - x0)) });
    const soltar = () => {
      document.removeEventListener('pointermove', mover);
      document.removeEventListener('pointerup', soltar);
      document.body.classList.remove('ck-redimensionando');
      gravarLarg();
    };
    document.addEventListener('pointermove', mover);
    document.addEventListener('pointerup', soltar);
  };
  const resetarLarg = (chave) => {
    const l = { ...largRef.current };
    delete l[chave];
    salvarLarg(l);
    gravarLarg();
  };
  const admin = useMemo(souAdmin, []);
  const podeVerHistorico = useMemo(souAdminOuGestor, []);
  const [baixando, setBaixando] = useState(false);
  const [hist, setHist] = useState({ id: null, carregando: false, erro: '', dados: null });

  const [itens, setItens] = useState([]);
  const [agoraRepique, setAgoraRepique] = useState(Date.now());
  useEffect(() => {
    const relogio = window.setInterval(() => setAgoraRepique(Date.now()), 60000);
    return () => window.clearInterval(relogio);
  }, []);
  const [loading, setLoading] = useState(true);
  const [erroLista, setErroLista] = useState('');
  const [busca, setBusca] = useState('');
  const [periodo, setPeriodo] = useState({ de: '', ate: '' });
  const [dia, setDia] = useState(hojeISO());
  const [barraFiltro, setBarraFiltro] = useState(false);
  const [colFiltros, setColFiltros] = useState({});
  const [ordem, setOrdem] = useState({ chave: 'data', dir: -1 });
  const [sel, setSel] = useState({});
  const [copiadas, setCopiadas] = useState(lerCopiadas);
  const [colando, setColando] = useState(false);
  const podeColar = !!copiadas && copiadas.tipo === tipoSeguro && copiadas.linhas.length > 0;
  const [popup, setPopup] = useState(null);
  const [aviso, setAviso] = useState('');
  const [todasDatas, setTodasDatas] = useState(false);   // marcada: a busca procura em todas as datas
  const achadasRef = useRef(0);

  const itensRef = useRef([]);
  const timers = useRef({});
  const pendentes = useRef({});
  const criando = useRef({});
  const tocados = useRef({}); // linhas editadas continuam visíveis até o filtro mudar
  itensRef.current = itens;

  const atualizar = (fn) => {
    const novo = fn(itensRef.current);
    itensRef.current = novo;
    setItens(novo);
  };

  const mostrar = (msg, ms = 1500) => {
    setAviso(msg);
    clearTimeout(timers.current.__aviso);
    timers.current.__aviso = setTimeout(() => setAviso(''), ms);
  };

  const reqCarga = useRef(0);   // ignora respostas antigas se o usuário trocar o dia rápido
  const carregar = async () => {
    const meu = ++reqCarga.current;
    setLoading(true);
    setErroLista('');
    try {
      const p = new URLSearchParams({ tipo: tipoSeguro });
      const termo = todasDatas ? busca.trim() : '';
      if (termo) { p.set('q', termo); p.set('limite', String(LIMITE_BUSCA_TODAS)); }
      else if (dia) p.set('dia', dia);
      else { if (periodo.de) p.set('de', periodo.de); if (periodo.ate) p.set('ate', periodo.ate); }
      const dados = await api(`?${p.toString()}`);
      if (meu !== reqCarga.current) return;
      const doServidor = Array.isArray(dados) ? dados : [];
      // linhas novas ainda não salvas continuam na tela
      atualizar((atual) => [...atual.filter((i) => isRascunho(i.id) && !criando.current[i.id]), ...doServidor]);
    } catch (err) {
      if (meu !== reqCarga.current) return;
      setErroLista(err.message);
      atualizar(() => []);
    } finally {
      if (meu === reqCarga.current) setLoading(false);
    }
  };

  const enviar = async (id) => {
    const linha = itensRef.current.find((i) => i.id === id);
    if (!linha) return;

    if (isRascunho(id)) {
      if (criando.current[id]) return;
      if (!(linha.paciente || '').trim() || !linha.data) return;
      criando.current[id] = true;
      delete pendentes.current[id];
      try {
        setAviso('Salvando...');
        const novo = await api('', { method: 'POST', body: JSON.stringify({ ...linha, status: linha.status || 'NOVO', tipo: tipoSeguro }) });
        const resto = pendentes.current[id];
        delete pendentes.current[id];
        delete criando.current[id];
        if (tocados.current[id]) { tocados.current[novo.id] = true; delete tocados.current[id]; }
        atualizar((a) => a.map((i) => (i.id === id ? { ...i, id: novo.id, atendente: novo.atendente } : i)));
        mostrar('Salvo');
        if (resto) { pendentes.current[novo.id] = resto; enviar(novo.id); }
      } catch (err) {
        delete criando.current[id];
        mostrar(err.message, 4000);
      }
      return;
    }

    const campos = pendentes.current[id];
    if (!campos) return;
    delete pendentes.current[id];
    try {
      setAviso('Salvando...');
      await api(`/${id}`, { method: 'PUT', body: JSON.stringify(campos) });
      mostrar('Salvo');
    } catch (err) {
      mostrar(err.message, 4000);
    }
  };

  const marcarRepiqueVisualizado = (item) => {
    if (!item || !item.repique_pendente || isRascunho(item.id)) return;

    // Para a animação imediatamente na tela.
    atualizar((listaAtual) => listaAtual.map((registro) => (
      registro.id === item.id
        ? { ...registro, repique_pendente: false }
        : registro
    )));

    // Persiste somente a confirmação de leitura; o contador permanece igual.
    pendentes.current[item.id] = {
      ...(pendentes.current[item.id] || {}),
      repique_pendente: false
    };

    clearTimeout(timers.current[item.id]);
    timers.current[item.id] = setTimeout(() => enviar(item.id), 0);
  };

  const editar = (id, chave, valor, imediato = false) => {
    tocados.current[id] = true;
    atualizar((a) => a.map((i) => (i.id === id ? { ...i, [chave]: valor } : i)));
    const vazio = !String(valor).trim();
    if (!isRascunho(id) && vazio && (chave === 'paciente' || chave === 'data')) return;
    pendentes.current[id] = { ...(pendentes.current[id] || {}), [chave]: valor };
    clearTimeout(timers.current[id]);
    timers.current[id] = setTimeout(() => enviar(id), imediato ? 0 : 600);
  };

  const limparFiltros = () => {
    tocados.current = {};
    setBusca('');
    setTodasDatas(false);
    setPeriodo({ de: '', ate: '' });
    setDia(hojeISO());
    setColFiltros({});
  };
  const mudarPeriodo = (p) => { tocados.current = {}; setDia(''); setPeriodo((o) => ({ ...o, ...p })); };
  const mudarDia = (v) => { tocados.current = {}; setPeriodo({ de: '', ate: '' }); setDia(v); };
  const somaDia = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return isoDe(d); };
  const mudarColFiltro = (chave, vals) => { tocados.current = {}; setColFiltros((f) => ({ ...f, [chave]: vals })); };

  useEffect(() => {
    limparFiltros(); setBarraFiltro(false); setSel({}); setPopup(null);
    atualizar(() => []);
    return () => {
      Object.keys(timers.current).forEach((k) => clearTimeout(timers.current[k]));
      Object.keys(pendentes.current).forEach((id) => enviar(id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoSeguro]);

  // Carrega só o que está na tela: o dia, o período, ou o resultado da busca em todas as datas.
  const buscaServidor = todasDatas ? busca.trim() : '';
  useEffect(() => {
    // grava edições pendentes antes de trocar a lista, para não perder nada
    Object.keys(pendentes.current).forEach((id) => { clearTimeout(timers.current[id]); enviar(id); });
    const t = setTimeout(carregar, buscaServidor ? 400 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoSeguro, dia, periodo.de, periodo.ate, buscaServidor]);

  useEffect(() => {
    const dentro = (e) => {
      const path = e.composedPath ? e.composedPath() : [];
      return path.some((n) => n.classList && (n.classList.contains('ck-pop') || n.classList.contains('ck-pop-btn')));
    };
    const fechar = (e) => { if (!dentro(e)) setPopup(null); };
    const rolou = (e) => {
      if (e.target && e.target.closest && e.target.closest('.ck-pop')) return;
      setPopup((p) => (p && p.tipo ? null : p));
    };
    document.addEventListener('click', fechar);
    window.addEventListener('scroll', rolou, true);
    return () => {
      document.removeEventListener('click', fechar);
      window.removeEventListener('scroll', rolou, true);
    };
  }, []);

  // Registra tentativa de print. O navegador só permite ver as teclas de captura e a impressão.
  useEffect(() => {
    let ultimo = 0;
    const avisar = (detalhe) => {
      if (Date.now() - ultimo < 3000) return;
      ultimo = Date.now();
      api('/print', { method: 'POST', body: JSON.stringify({ tipo: tipoSeguro, detalhe }) }).catch(() => {});
    };
    const aoTecla = (e) => {
      if (e.key === 'PrintScreen' || e.code === 'PrintScreen') return avisar('PrintScreen');
      if (e.metaKey && e.shiftKey && e.code === 'KeyS') return avisar('Win/Cmd + Shift + S');
      if (e.metaKey && e.shiftKey && ['Digit3', 'Digit4', 'Digit5'].includes(e.code)) return avisar('Cmd + Shift + 3/4/5');
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyP') return avisar('Imprimir (Ctrl/Cmd + P)');
      return undefined;
    };
    const aoImprimir = () => avisar('Imprimir (menu do navegador)');
    window.addEventListener('keydown', aoTecla);
    window.addEventListener('keyup', aoTecla);
    window.addEventListener('beforeprint', aoImprimir);
    return () => {
      window.removeEventListener('keydown', aoTecla);
      window.removeEventListener('keyup', aoTecla);
      window.removeEventListener('beforeprint', aoImprimir);
    };
  }, [tipoSeguro]);

  const lista = useMemo(() => {
    const t = busca.toLowerCase().trim();
    const digitos = t.replace(/\D/g, '');
    const emTodas = todasDatas && !!t;   // busca em todas as datas: só vale com algo digitado
    const f = itens.filter((i) => {
      if (isRascunho(i.id)) return true;
      if (!tocados.current[i.id]) {
        const d = valorCol(i, 'data');
        if (!emTodas) {
          if (dia && d !== dia) return false;
          if (periodo.de && d < periodo.de) return false;
          if (periodo.ate && d > periodo.ate) return false;
        }
        const barrado = Object.keys(colFiltros).some((k) => {
          if (emTodas && k === 'data') return false;
          const s = colFiltros[k];
          return s && s.length > 0 && !s.includes(valorCol(i, k));
        });
        if (barrado) return false;
      }
      if (!t) return true;
      const texto = [i.paciente, i.telefone, i.servico, i.especialidade, i.procedimento, i.profissional, i.associado, i.observacoes]
        .some((v) => (v || '').toLowerCase().includes(t));
      return texto || (digitos && (i.telefone || '').replace(/\D/g, '').includes(digitos));
    });
    const ordenada = f.sort((a, b) => String(a[ordem.chave] || '').localeCompare(String(b[ordem.chave] || ''), 'pt-BR') * ordem.dir);
    achadasRef.current = ordenada.length;
    if (!emTodas || ordenada.length <= LIMITE_BUSCA_TODAS) return ordenada;
    // muitas linhas achadas: mostra as primeiras e mantém as linhas novas ou em edição
    return ordenada.filter((i, k) => k < LIMITE_BUSCA_TODAS || isRascunho(i.id) || tocados.current[i.id]);
  }, [itens, busca, dia, periodo, colFiltros, ordem, todasDatas]);

  const selecionados = lista.filter((i) => sel[i.id]);
  const todos = lista.length > 0 && selecionados.length === lista.length;
  const filtrando = !!(busca || dia || periodo.de || periodo.ate || Object.values(colFiltros).some((v) => v && v.length));

  const opcoesCol = (chave) => {
    const doEscopo = itens.filter((i) => {
      if (isRascunho(i.id)) return false;
      const d = valorCol(i, 'data');
      if (dia && d !== dia) return false;
      if (periodo.de && d < periodo.de) return false;
      if (periodo.ate && d > periodo.ate) return false;
      return true;
    });
    const fixos = chave === 'origem' ? ORIGENS.map(norm) : [];
    const set = new Set([...fixos, ...doEscopo.map((i) => valorCol(i, chave)), ...(colFiltros[chave] || [])]);
    return [...set].sort((a, b) => a.localeCompare(b, 'pt-BR')).map((v) => ({
      valor: v, label: v === '' ? 'Em branco' : (chave === 'data' ? fmtData(v) : v),
    }));
  };

  const adicionar = () => {
    const nova = linhaNova();
    limparFiltros();
    atualizar((a) => [nova, ...a]);
    setTimeout(() => {
      const el = document.querySelector(`[data-id="${nova.id}"][data-c="paciente"]`);
      if (el) el.focus();
    }, 40);
  };

  const remover = async (i) => {
    delete pendentes.current[i.id];
    clearTimeout(timers.current[i.id]);
    if (isRascunho(i.id)) { atualizar((a) => a.filter((x) => x.id !== i.id)); return; }
    if (ehAntigo(i)) { mostrar('Registros com mais de 1 mês não podem ser excluídos, apenas editados.', 4000); return; }
    if (!window.confirm(`Remover o atendimento de ${i.paciente || 'este registro'}?`)) return;
    try {
      await api(`/${i.id}`, { method: 'DELETE' });
      atualizar((a) => a.filter((x) => x.id !== i.id));
    } catch (err) {
      mostrar(err.message, 4000);
    }
  };

  const aplicarCor = (ids, cor) => {
    ids.forEach((id) => editar(id, 'cor', cor, true));
    setPopup(null);
  };

  const abrirPopup = (e, info, larg, alt, direita) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.max(8, Math.min(direita ? r.right - larg : r.left, window.innerWidth - larg - 8));
    const y = Math.max(8, Math.min(r.bottom + 4, window.innerHeight - alt - 8));
    setPopup((p) => (p && p.tipo === info.tipo && p.id === info.id && p.chave === info.chave ? null : { ...info, x, y }));
  };

  const copiar = async () => {
    const base = selecionados.length ? selecionados : lista;
    const txt = base.map((i) => cfg.colunas.map((c) => (c.chave === 'data' ? fmtData(i.data) : i[c.chave] || '')).join('\t')).join('\n');
    // guarda as linhas para colar em outra data (só os campos de cadastro: a data e o atendente não vão)
    const campos = [...new Set(cfg.colunas.map((c) => c.chave))].filter((k) => k !== 'data' && k !== 'atendente');
    const linhas = base
      .filter((i) => (i.paciente || '').trim())
      .map((i) => Object.fromEntries([...campos, 'cor'].filter((k) => i[k] !== undefined && i[k] !== null).map((k) => [k, i[k]])));
    const memoria = { tipo: tipoSeguro, em: Date.now(), linhas };
    guardarCopiadas(memoria);
    setCopiadas(memoria);
    try { await navigator.clipboard.writeText(txt); mostrar(`${base.length} linha(s) copiada(s) — use Colar em outra data`, 2500); }
    catch (err) { mostrar('Linhas guardadas para colar (o navegador bloqueou a cópia do texto)', 3500); }
  };

  const colar = async () => {
    if (colando) return;
    if (!podeColar) { mostrar('Copie uma ou mais linhas deste checklist antes de colar.', 3500); return; }
    if (!dia) { mostrar('Escolha um dia (setas da data, no alto da tela) para colar.', 3500); return; }
    const n = copiadas.linhas.length;
    if (n > COPIA_LIMITE) { mostrar(`Dá para colar até ${COPIA_LIMITE} linhas de uma vez (copiadas: ${n}).`, 4000); return; }
    if (!window.confirm(`Colar ${n} linha(s) em ${fmtData(dia)}?`)) return;
    setColando(true);
    const vazia = Object.fromEntries(cfg.colunas.map((c) => [c.chave, '']));
    const marca = Date.now();
    const novas = copiadas.linhas.map((l, k) => ({
      ...vazia,
      ...l,
      ...(COLAR_COM_STATUS_NOVO ? { status: 'NOVO' } : {}),
      id: `rascunho-${marca}-${k}`,
      data: dia,
      atendente: '',
    }));
    novas.forEach((l) => { tocados.current[l.id] = true; });
    atualizar((a) => [...novas, ...a]);
    setSel({});
    for (const l of novas) { await enviar(l.id); }
    setColando(false);
    mostrar(`${n} linha(s) colada(s) em ${fmtData(dia)}`, 2500);
  };

  const baixarExcel = async () => {
    const ids = lista.map((i) => i.id).filter((id) => !isRascunho(id));
    if (!ids.length) { mostrar('Não há registros para baixar', 3000); return; }
    setBaixando(true);
    try {
      // garante que edições recém-digitadas já foram salvas
      await Promise.all(Object.keys(pendentes.current).map((id) => enviar(id)));
      const res = await fetch('/api/checklist/exportar', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: tipoSeguro, ids }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || 'Erro ao gerar o Excel');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `checklist-${tipoSeguro}-${hojeISO()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      mostrar(`${ids.length} registro(s) exportado(s)`, 2500);
    } catch (err) {
      mostrar(err.message, 4000);
    } finally {
      setBaixando(false);
    }
  };

  // Mais de 1 mês desde a criação do registro: só pode editar, não excluir.
  const ehAntigo = (i) => !isRascunho(i.id) && i.created_date && new Date(i.created_date) < umMesAtras();

  const carregarHistorico = async (id) => {
    setHist({ id, carregando: true, erro: '', dados: null });
    try {
      const dados = await api(`/${id}/historico`);
      setHist({ id, carregando: false, erro: '', dados });
    } catch (err) {
      setHist({ id, carregando: false, erro: err.message, dados: null });
    }
  };

  const ordenarPor = (chave) => setOrdem((o) => (o.chave === chave ? { chave, dir: -o.dir } : { chave, dir: 1 }));

  const aoTeclar = (e) => {
    const celulaAtual = e.target.closest('[data-id][data-c]')
      || e.target.closest('td')?.querySelector('[data-id][data-c]');
    if (!celulaAtual || e.target.disabled) return;

    const teclasVerticais = ['ArrowUp', 'ArrowDown', 'Enter'];
    const teclasHorizontais = ['ArrowLeft', 'ArrowRight'];
    if (!teclasVerticais.includes(e.key) && !teclasHorizontais.includes(e.key)) return;

    // Nas caixas de texto, as setas laterais movem o cursor.
    // A navegação entre células começa no início/fim do texto.
    if (
      teclasHorizontais.includes(e.key)
      && e.target instanceof HTMLInputElement
      && e.target.type === 'text'
    ) {
      const inicio = e.target.selectionStart;
      const fim = e.target.selectionEnd;
      const tamanho = e.target.value.length;
      if (e.key === 'ArrowLeft' && inicio > 0) return;
      if (e.key === 'ArrowRight' && fim < tamanho) return;
    }

    const tbody = e.target.closest('tbody');
    const linhaAtual = e.target.closest('tr');
    if (!tbody || !linhaAtual) return;

    let proximo = null;

    if (teclasHorizontais.includes(e.key)) {
      const celulas = Array.from(linhaAtual.querySelectorAll('[data-id][data-c]'))
        .filter((celula) => !celula.disabled);
      const indice = celulas.indexOf(celulaAtual);
      const passo = e.key === 'ArrowRight' ? 1 : -1;
      proximo = celulas[indice + passo] || null;
    } else {
      const linhas = Array.from(tbody.querySelectorAll('tr'))
        .filter((linha) => linha.querySelector('[data-id][data-c]'));
      const indiceLinha = linhas.indexOf(linhaAtual);
      const passo = e.key === 'ArrowUp' ? -1 : 1;

      // Enter mantém o comportamento existente: desce na mesma coluna.
      for (let i = indiceLinha + passo; i >= 0 && i < linhas.length; i += passo) {
        const candidata = linhas[i].querySelector(
          `[data-id][data-c="${celulaAtual.dataset.c}"]`
        );
        if (candidata && !candidata.disabled) {
          proximo = candidata;
          break;
        }
      }
    }

    if (proximo) {
      e.preventDefault();
      proximo.focus();
    }
  };

  const hoje = hojeISO();
  const rotuloDia = !dia ? 'Todos os dias'
    : new Date(`${dia}T12:00:00`).toLocaleDateString('pt-BR', dia === hoje
      ? { day: 'numeric', month: 'long' }
      : { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
  const tituloDia = dia === hoje ? `Hoje, ${rotuloDia}` : rotuloDia;

  const celula = (col, i) => {
    const c = col.chave;
    if (col.tipo === 'auto') {
      return <td key={c}><input className="ck-cel" disabled value={i.atendente || ''} placeholder="Automático" /></td>;
    }
    if (col.tipo === 'date') {
      const invalido = !isRascunho(i.id) && !i.data;
      return (
        <td key={c}>
          <button type="button" data-id={i.id} data-c={c}
            className={`ck-cel ck-data-btn ck-pop-btn${invalido ? ' ck-invalido' : ''}`}
            onClick={(e) => abrirPopup(e, { tipo: 'cal', id: i.id }, 232, 320, false)}>
            <span>{fmtData(i.data) || 'dd/mm/aaaa'}</span><Calendar size={14} />
          </button>
        </td>
      );
    }
    if (col.tipo === 'status') {
      const cls = classeStatus(i.status);
      return (
        <td key={c}>
          <div className="ck-sel-wrap ck-st-wrap">
            <span className={`ck-dot ck-dot-${cls}`} />
            <input className={`ck-cel ck-st ck-st-${cls}`} data-id={i.id} data-c={c}
              value={i.status || ''} placeholder={col.placeholder}
              onChange={(e) => editar(i.id, c, e.target.value)} />
            <select className="ck-st-pick" value="" aria-label="Escolher status"
              onChange={(e) => e.target.value && editar(i.id, c, e.target.value, true)}>
              <option value="" />
              {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <ChevronDown size={13} className="ck-st-seta" />
          </div>
        </td>
      );
    }
    if (c === 'repique') {
      const atual = Number(i.repique || 0);
      const selecionarRepique = (valor) => {
        const numero = Number(valor);
        if (!Number.isInteger(numero) || numero < 1 || numero > 20) return;

        const inicio = (!i.repique_started_at || numero !== atual)
          ? new Date().toISOString()
          : i.repique_started_at;

        editar(i.id, 'repique', numero, true);
        editar(i.id, 'repique_started_at', inicio, true);
        setAgoraRepique(Date.now());
      };

      return (
        <td key={c}>
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
            <div className="ck-sel-wrap ck-st-wrap">
              <input
                className="ck-cel ck-combo"
                data-id={i.id}
                data-c={c}
                value={String(atual)}
                readOnly
                aria-label={`Repique ${atual}`}
              />
              <select
                className="ck-st-pick"
                value=""
                aria-label="Selecionar número do repique"
                onChange={(e) => e.target.value && selecionarRepique(e.target.value)}
              >
                <option value="" />
                {REPIQUES.map((numero) => (
                  <option key={numero} value={numero}>{numero}</option>
                ))}
              </select>
              <ChevronDown size={13} className="ck-st-seta" />
            </div>
            {atual > 0 && i.repique_started_at && (
              <span style={{ padding: '0 8px 3px', color: '#64748b', fontSize: 10, lineHeight: '12px', whiteSpace: 'nowrap' }}>
                {formatTempoRepique(i.repique_started_at, agoraRepique)}
              </span>
            )}
          </div>
        </td>
      );
    }
    if (col.tipo === 'combo') {
      return (
        <td key={c}>
          <div className="ck-sel-wrap ck-st-wrap">
            <input className="ck-cel ck-combo" data-id={i.id} data-c={c}
              value={i[c] || ''} placeholder={col.placeholder}
              onChange={(e) => editar(i.id, c, e.target.value)} />
            <select className="ck-st-pick" value="" aria-label={`Escolher ${col.rotulo}`}
              onChange={(e) => e.target.value && editar(i.id, c, e.target.value, true)}>
              <option value="" />
              {col.opcoes.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <ChevronDown size={13} className="ck-st-seta" />
          </div>
        </td>
      );
    }
    if (col.tipo === 'select') {
      const ops = i[c] && !col.opcoes.includes(i[c]) ? [...col.opcoes, i[c]] : col.opcoes;
      return (
        <td key={c}>
          <div className="ck-sel-wrap">
            <select className="ck-cel ck-sel" data-id={i.id} data-c={c} value={i[c] || ''}
              onChange={(e) => editar(i.id, c, e.target.value, true)}>
              <option value="">{col.rotulo}</option>
              {ops.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </td>
      );
    }
    if (col.tipo === 'tel') {
      // Telefone igual ao Paciente: campo de texto direto na celula (sem link e sem lapis), com a mascara (11) 99999-9999.
      return (
        <td key={c}>
          <input
            className="ck-cel"
            data-id={i.id}
            data-c={c}
            type="text"
            placeholder={col.placeholder || ''}
            aria-label={`Telefone do paciente ${i.paciente || ''}`.trim()}
            value={i[c] || ''}
            onChange={(e) => editar(i.id, c, mascaraTelefone(e.target.value))}
          />
        </td>
      );
    }
    const invalido = !isRascunho(i.id) && c === 'paciente' && !i[c];
    return (
      <td key={c}>
        <input className={`ck-cel${invalido ? ' ck-invalido' : ''}`} data-id={i.id} data-c={c}
          type="text" placeholder={col.placeholder || ''} value={i[c] || ''}
          onChange={(e) => editar(i.id, c, col.tipo === 'tel' ? mascaraTelefone(e.target.value) : e.target.value)} />
      </td>
    );
  };

  const colPopup = popup && popup.tipo === 'col' ? cfg.colunas.find((c) => c.chave === popup.chave) : null;
  const linhaPopup = popup && popup.id ? itens.find((i) => i.id === popup.id) : null;

  return (
    <div className={`ck-page ck-tipo-${tipoSeguro}`}>
      <div className="ck-topo">
        <div>
          <div className="ck-eyebrow">Painel operacional · {cfg.titulo}</div>
          <h1 className="ck-titulo">Visão geral</h1>
          <p className="ck-sub">Acompanhe os atendimentos de {cfg.titulo.toLowerCase()} em um só lugar.</p>
        </div>
        <div className="ck-topo-acoes">
          <div className="ck-dia">
            <button type="button" className="ck-dia-seta" aria-label="Dia anterior"
              onClick={() => mudarDia(somaDia(dia || hoje, -1))}><ChevronLeft size={16} /></button>
            <button type="button" className={`ck-dia-btn ck-pop-btn${dia !== hoje ? ' ck-ativo' : ''}`}
              onClick={(e) => abrirPopup(e, { tipo: 'dia' }, 232, 320, true)}>
              <Calendar size={14} /> {tituloDia}
            </button>
            <button type="button" className="ck-dia-seta" aria-label="Próximo dia"
              onClick={() => mudarDia(somaDia(dia || hoje, 1))}><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>

      <div className="ck-secao-topo">
        <div>
          <h2 className="ck-secao-titulo">Atendimentos</h2>
          <p className="ck-sub">Filtre, encontre e atualize sem sair da tabela.</p>
        </div>
        <span className="ck-protegido"><Shield size={12} /> Dados protegidos</span>
      </div>

      <div className="ck-tabela-caixa">
        <div className="ck-barra">
          <button className="ck-tb-add" onClick={adicionar}><Plus size={15} /> {cfg.add}</button>

          <button className={`ck-tb-btn${barraFiltro || periodo.de || periodo.ate ? ' ck-ativo' : ''}`} onClick={() => setBarraFiltro((v) => !v)}>
            <Filter size={14} /> Filtrar
          </button>

          <div className="ck-pos">
            <button className="ck-tb-btn ck-pop-btn" onClick={() => setPopup(popup === 'ordem' ? null : 'ordem')}><ArrowUpDown size={14} /> Ordenar</button>
            {popup === 'ordem' && (
              <div className="ck-pop">
                <h4>Coluna</h4>
                <select value={ordem.chave} onChange={(e) => setOrdem((o) => ({ ...o, chave: e.target.value }))}>
                  {cfg.colunas.map((c) => <option key={c.chave} value={c.chave}>{c.rotulo}</option>)}
                </select>
                <h4>Direção</h4>
                <select value={ordem.dir} onChange={(e) => setOrdem((o) => ({ ...o, dir: Number(e.target.value) }))}>
                  <option value={1}>Crescente</option>
                  <option value={-1}>Decrescente</option>
                </select>
              </div>
            )}
          </div>

          <div className="ck-pos">
            <button className="ck-tb-btn ck-pop-btn" onClick={() => setPopup(popup === 'cor' ? null : 'cor')}><Palette size={14} /> Alterar cor</button>
            {popup === 'cor' && (
              <div className="ck-pop">
                {selecionados.length === 0
                  ? <div className="ck-pop-aviso">Marque a caixa de uma ou mais linhas para mudar a cor.</div>
                  : <Paleta onEscolher={(cor) => aplicarCor(selecionados.map((i) => i.id), cor)} />}
              </div>
            )}
          </div>

          <button className="ck-tb-btn" onClick={copiar}><Copy size={14} /> Copiar</button>
          <button className="ck-tb-btn" onClick={colar} disabled={colando || !podeColar}
            title={podeColar ? `Colar ${copiadas.linhas.length} linha(s) em ${dia ? fmtData(dia) : 'um dia escolhido'}` : 'Copie linhas deste checklist para colar em outra data'}>
            <ClipboardPaste size={14} /> {colando ? 'Colando...' : `Colar${podeColar ? ` (${copiadas.linhas.length})` : ''}`}
          </button>
          {admin && (
            <button className="ck-tb-btn" onClick={baixarExcel} disabled={baixando}>
              <Download size={14} /> {baixando ? 'Gerando...' : 'Baixar Excel'}
            </button>
          )}
          <button className="ck-tb-btn" onClick={limparFiltros}><RotateCcw size={14} /> Limpar filtros</button>

          <label className="ck-todas-datas" title="Marcada: a busca procura o nome ou telefone em todas as datas">
            <input type="checkbox" checked={todasDatas} onChange={(e) => { tocados.current = {}; setTodasDatas(e.target.checked); }} />
            Todas as datas
          </label>
          {todasDatas && busca.trim() !== '' && (
            <span className="ck-todas-info">{achadasRef.current} achada(s){achadasRef.current > lista.length ? ` — mostrando ${lista.length}` : ''}</span>
          )}
          <div className="ck-busca ck-busca-barra">
            <Search size={15} />
            <input type="text" placeholder={todasDatas ? 'Buscar em todas as datas' : 'Buscar paciente ou telefone'} value={busca} onChange={(e) => { tocados.current = {}; setBusca(e.target.value); }} />
          </div>
        </div>

        {barraFiltro && (
          <div className="ck-periodo">
            <span className="ck-periodo-tit"><Calendar size={14} /> Filtrar por período</span>
            <label>De <input type="date" value={periodo.de} max={periodo.ate || undefined} onChange={(e) => mudarPeriodo({ de: e.target.value })} /></label>
            <label>Até <input type="date" value={periodo.ate} min={periodo.de || undefined} onChange={(e) => mudarPeriodo({ ate: e.target.value })} /></label>
            <span className="ck-periodo-dica"><Filter size={12} /> Use os filtros nos cabeçalhos para refinar a lista</span>
          </div>
        )}

        <div className="ck-info">
          <span className="ck-total-linhas"><strong>Atendimentos</strong> · {lista.length} {lista.length === 1 ? 'linha' : 'linhas'}</span>
          <span className="ck-total-agendados" title="Atendimentos com status AGENDADO na lista atual">Agendados: <strong>{lista.filter((l) => String(l.status == null ? '' : l.status).trim().toUpperCase() === 'AGENDADO').length}</strong></span>
          <span className="ck-aviso">{aviso}</span>
          <span className="ck-dica"><kbd>Tab</kbd> navegar &nbsp; <kbd>Enter</kbd> descer</span>
        </div>

        <div className="ck-tabela-rolagem">
          <table className="ck-tabela ck-edit" style={{ width: 36 + cfg.colunas.reduce((t, c) => t + largDe(c), 0) + 112 }}>
            <colgroup>
              <col style={{ width: 36 }} />
              {cfg.colunas.map((c) => <col key={c.chave + c.rotulo} style={{ width: largDe(c) }} />)}
              <col style={{ width: 112 }} />
            </colgroup>
            <thead>
              <tr>
                <th className="ck-td-ck">
                  <input type="checkbox" checked={todos} aria-label="Selecionar todas"
                    onChange={() => setSel(todos ? {} : Object.fromEntries(lista.map((i) => [i.id, true])))} />
                </th>
                {cfg.colunas.map((c) => {
                  const ativo = (colFiltros[c.chave] || []).length > 0;
                  return (
                    <th key={c.chave + c.rotulo}>
                      <span className="ck-resize" title="Arraste para ajustar a largura (duplo clique restaura)"
                        onPointerDown={(e) => iniciarResize(e, c.chave)}
                        onDoubleClick={() => resetarLarg(c.chave)}
                        onClick={(e) => e.stopPropagation()} />
                      <div className="ck-th">
                        <span className="ck-th-nome" onClick={() => ordenarPor(c.chave)}>
                          {c.rotulo} <span className="ck-seta">{ordem.chave === c.chave ? (ordem.dir > 0 ? '▲' : '▼') : ''}</span>
                        </span>
                        <button type="button" className={`ck-th-f ck-pop-btn${ativo ? ' ck-ativo' : ''}`}
                          aria-label={`Filtrar ${c.rotulo}`}
                          onClick={(e) => abrirPopup(e, { tipo: 'col', chave: c.chave }, 230, 330, true)}>
                          <Filter size={11} />
                        </button>
                      </div>
                    </th>
                  );
                })}
                <th className="ck-th-mais">···</th>
              </tr>
            </thead>
            <tbody onKeyDown={aoTeclar}>
              {loading && (<tr><td colSpan={cfg.colunas.length + 2} className="ck-vazio">Carregando...</td></tr>)}
              {!loading && erroLista && (<tr><td colSpan={cfg.colunas.length + 2} className="ck-vazio ck-erro">{erroLista}</td></tr>)}
              {!loading && !erroLista && lista.length === 0 && (
                <tr><td colSpan={cfg.colunas.length + 2} className="ck-vazio">Nenhum atendimento neste filtro. Escolha outro dia no calendário do topo, limpe os filtros ou adicione um registro.</td></tr>
              )}
              {!loading && !erroLista && lista.map((i) => (
                <tr
                  key={i.id}
                  className={`ck-linha-${classeStatus(i.status)}${sel[i.id] ? ' ck-linha-marcada' : ''}${i.repique_pendente ? ' ck-linha-repique-pendente' : ''}`}
                  style={i.cor ? { background: i.cor } : undefined}
                  onClick={() => marcarRepiqueVisualizado(i)}
                >
                  <td className="ck-td-ck">
                    <input type="checkbox" checked={!!sel[i.id]} aria-label="Selecionar linha"
                      onChange={() => setSel((s) => ({ ...s, [i.id]: !s[i.id] }))} />
                  </td>
                  {cfg.colunas.map((c) => celula(c, i))}
                  <td className="ck-acoes">
                    <button className="ck-pop-btn" title="Cor da linha" onClick={(e) => abrirPopup(e, { tipo: 'linhaCor', id: i.id }, 190, 260, true)}><Palette size={15} /></button>
                    <button className="ck-excluir" disabled={!!ehAntigo(i)} title={ehAntigo(i) ? 'Registros com mais de 1 mês não podem ser excluídos, apenas editados' : 'Excluir'} onClick={() => remover(i)}><Trash2 size={15} /></button>
                    {podeVerHistorico && !isRascunho(i.id) && (
                      <button className="ck-pop-btn" title="Histórico de modificações" aria-label="Histórico"
                        onClick={(e) => { abrirPopup(e, { tipo: 'hist', id: i.id }, 300, 300, true); carregarHistorico(i.id); }}><Eye size={15} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {popup && popup.tipo && (
        <div className={`ck-pop ck-pop-fixo${popup.tipo === 'col' ? ' ck-pop-col' : ''}`} style={{ left: popup.x, top: popup.y }}>
          {popup.tipo === 'linhaCor' && (
            <Paleta atual={(linhaPopup || {}).cor || ''} onEscolher={(cor) => aplicarCor([popup.id], cor)} />
          )}
          {popup.tipo === 'cal' && (
            <Calendario valor={(linhaPopup || {}).data ? String(linhaPopup.data).slice(0, 10) : ''}
              onEscolher={(v) => { editar(popup.id, 'data', v, true); setPopup(null); }} />
          )}
          {popup.tipo === 'hist' && (
            <div className="ck-hist">
              <h4>Últimas modificações</h4>
              {hist.carregando && <div className="ck-pop-aviso">Carregando...</div>}
              {hist.erro && <div className="ck-pop-aviso ck-erro">{hist.erro}</div>}
              {hist.dados && hist.id === popup.id && (
                <>
                  {hist.dados.modificacoes.map((m, k) => (
                    <div key={k} className="ck-hist-item">
                      <b>{m.usuario || 'Sem nome'}</b>
                      <span>{fmtHora(m.created_date)}</span>
                      <em>{m.acao === 'criou' ? 'Criou o registro' : `Alterou: ${m.detalhe}`}</em>
                    </div>
                  ))}
                  <h4 className="ck-hist-sep">Tentativas de print desta tela</h4>
                  {hist.dados.prints.length === 0
                    ? <div className="ck-pop-aviso">Nenhuma tentativa registrada.</div>
                    : hist.dados.prints.map((m, k) => (
                      <div key={k} className="ck-hist-item ck-hist-print">
                        <b>{m.usuario || 'Sem nome'}</b>
                        <span>{fmtHora(m.created_date)}</span>
                        <em>{m.detalhe}</em>
                      </div>
                    ))}
                </>
              )}
            </div>
          )}
          {popup.tipo === 'dia' && (
            <Calendario valor={dia}
              onEscolher={(v) => { mudarDia(v); setPopup(null); }} />
          )}
          {popup.tipo === 'col' && colPopup && (
            <FiltroColuna rotulo={colPopup.rotulo} opcoes={opcoesCol(popup.chave)}
              escopo={dia ? (dia === hojeISO() ? 'Somente de hoje' : `Somente de ${fmtData(dia)}`) : ((periodo.de || periodo.ate) ? 'Somente do período filtrado' : 'Todos os dias')}
              selecionados={colFiltros[popup.chave] || []}
              onChange={(vals) => mudarColFiltro(popup.chave, vals)}
              onFechar={() => setPopup(null)} />
          )}
        </div>
      )}

      <datalist id="ck-status-lista">{STATUS.map((s) => <option key={s} value={s} />)}</datalist>
    </div>
  );
}
